<template>
    <v-container id="personal-information" fluid>
        <v-card-subtitle class="px-0">
            <v-icon :color="kingsGold" left>mdi-account-box</v-icon>Basic Information
        </v-card-subtitle>
        <v-row>
            <v-col cols="12" md="4">
                <v-text-field v-model="item.firstName" dense label="First Name" outlined :rules="firstNameRules"
                    required>
                </v-text-field>
            </v-col>
            <v-col cols="12" md="4">
                <v-text-field v-model="item.lastName" dense label="Last Name" outlined :rules="lastNameRules" required>
                </v-text-field>
            </v-col>
            <v-col cols="12" md="4">
                <v-text-field v-model="item.preferredName" dense label="Preferred Name (Optional)" outlined>
                </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
                <v-menu ref="menu" v-model="dateOfBirthMenu" :close-on-content-click="false"
                    transition="scale-transition" offset-y min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="item.dateOfBirth" v-bind="attrs" v-on="on" dense label="Date of Birth"
                            outlined prepend-icon="mdi-calendar-account" readonly required :rules="dateOfBirthRules">
                        </v-text-field>
                    </template>
                    <v-date-picker ref="picker" v-model="item.dateOfBirth" :max="new Date().toISOString().substr(0, 10)"
                        min="1950-01-01" @change="saveDateOfBirth">
                    </v-date-picker>
                </v-menu>
            </v-col>
            <v-col cols="12" md="3">
                <v-select v-model="item.gender" dense :items="genderItems" label="Gender" outlined
                    placeholder="Please select..." required :rules="genderRules">
                </v-select>
            </v-col>
            <v-col cols="12" md="3">
                <v-text-field
                    v-model="item.prefpronoun"
                    outlined
                    dense
                    clearable
                    :rules="pronounRules"
                    label="Pronoun"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
                <v-checkbox
                    v-model="item.sixftorover"
                    label="6' or taller"
                    true-value="yes"
                    false-value="no"
                    @change="showHeightOptions"
                ></v-checkbox>
            </v-col>
            <v-col cols="12" md="6" v-if="showheightoptions">
                <v-select v-model="item.sixftoroverdetail" dense :items="sixftoroverdetailitems" label="Height: " outlined></v-select>
            </v-col>
        </v-row>
        <v-card-subtitle class="px-0">
            <v-icon :color="kingsGold" left>mdi-home</v-icon>Address Information
        </v-card-subtitle>
        <v-row>
            <v-col cols="12" md="8">
                <v-text-field v-model="item.address" dense label="Home Address" outlined :rules="addressRules" required>
                </v-text-field>
            </v-col>
            <v-col cols="12" md="4">
                <v-text-field v-model="item.postalCode" dense label="Postal Code" outlined :rules="postalCodeRules"
                    required>
                </v-text-field>
            </v-col>
            <v-col cols="12" md="4">
                <v-text-field v-model="item.country" dense label="Country" outlined persistent-hint
                    :rules="countryRules" required>
                </v-text-field>
            </v-col>
            <v-col cols="12" md="4">
                <v-text-field v-model="item.city" dense label="City" outlined :rules="cityRules" required>
                </v-text-field>
            </v-col>
            <v-col cols="12" md="4">
                <v-select v-model="item.province" dense :items="provinceItems" label="Province" outlined
                    placeholder="Please select..." :rules="provinceRules" required>
                </v-select>
            </v-col>
            <v-col v-if="item.province == 'Other'" cols="12">
                <v-text-field v-model="item.provinceOther" dense label="Province (Other)" outlined
                    :rules="provinceOtherRules" required>
                </v-text-field>
            </v-col>
        </v-row>
        <v-card-subtitle class="px-0">
            <v-icon :color="kingsGold" left>mdi-card-account-details</v-icon>Contact Information
        </v-card-subtitle>
        <v-row>
            <v-col cols="12" md="6">
                <v-text-field v-model="item.homePhone" dense label="Home Phone" outlined :rules="homePhoneRules"
                    required>
                </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
                <v-text-field v-model="item.cellPhone" dense label="Cell Phone" outlined :rules="cellPhoneRules"
                    required>
                </v-text-field>
            </v-col>
            <v-col cols="12">
                <v-text-field v-model="item.email" dense disabled label="Email" outlined :rules="emailRules" required>
                </v-text-field>
            </v-col>
            <v-col cols="6">
                <v-text-field v-model="item.emailPersonal" dense  label="Email (Personal)" outlined
                    required>
                </v-text-field>
            </v-col>
            <v-col cols="6">
                <v-text-field v-model="item.emailPersonalConfirmed" dense label="Confirm Email (Personal)" outlined :rules="[
                    v => v == item.emailPersonal || 'Email (personal) entries do not match',
                ]" required>
                </v-text-field>
            </v-col>
        </v-row>
        <v-card-subtitle class="px-0">
            <v-icon :color="kingsGold" left>mdi-school</v-icon>Academic Information
        </v-card-subtitle>
        <v-row>
            <v-col cols="12">
                <v-text-field v-model="item.studentnumber" :counter="9" dense :disabled="validStudentnumber"
                    label="Student Number" outlined :rules="studentnumberRules" required>
                </v-text-field>
            </v-col>
            <v-col cols="12">
                <v-select v-model="item.registeredAt" dense :items="formSettings.registeredAt" label="Registered at"
                    placeholder="Please select..." outlined required :rules="registeredAtRules">
                </v-select>
            </v-col>
            <v-col cols="12">
                <v-select v-model="item.yearOfStudy" dense hint="Note: During the upcoming academic year"
                    :items="formSettings.yearOfStudy" label="Year of study at King's University College" persistent-hint
                    placeholder="Please select..." outlined required :rules="yearOfStudyRules">
                </v-select>
            </v-col>
        </v-row>
        <v-card-subtitle class="px-0">
            <v-icon :color="kingsGold" left>mdi-calendar-range</v-icon>Residence Term
        </v-card-subtitle>
        <v-row>
            <v-alert v-if="residenceTermsAppliedTo.length > 0" icon="mdi-text-box-check" prominent text type="info"
                width="100%">
                This student has already applied for the following residence terms:
                <ul>
                    <li v-for="(item, i) in residenceTermsAppliedTo" :key="i">{{ item.residenceTerm }}
                    </li>
                </ul>
            </v-alert>
            <v-col cols="12">
                <v-select v-model="item.residenceTerm" dense :items="getResidenceTerms" label="Residence Term"
                    placeholder="Please select..." outlined required :rules="residenceTermRules">
                </v-select>
            </v-col>
        </v-row>
        <v-card-subtitle class="px-0">
            <v-icon :color="kingsGold" left>mdi-information</v-icon>Additional Information
        </v-card-subtitle>
        <v-row>
            <v-col cols="12">
                <v-select v-model="item.internationalStudent" dense :items="internationalStudentItems"
                    label="International Student" outlined placeholder="Please select..." required
                    :rules="internationalStudentRules">
                </v-select>
            </v-col>
            <v-col cols="12" class="pt-4">
                <v-textarea v-model="item.healthDietaryConcern" dense auto-grow
                    hint="Specify any health/dietary concern(s) that could be relevant to your stay in residence"
                    label="Health/Dietary Concern(s)" outlined persistent-hint>
                </v-textarea>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
export default {
    name: 'applicant-personal-information',
    props: {
        item: {
            required: true,
            type: Object
        },
        formSettings: {
            required: true
        },
        residenceTermsAppliedTo: {
            required: true
        }
    },
    data() {
        return {
            addressRules: [
                v => !!v || 'Home address is required',
                v => (v && v.length >= 2) || 'Please provide a valid home address',
            ],
            cellPhoneRules: [
                v => !!v || 'Cell phone is required',
            ],
            cityRules: [
                v => !!v || 'City is required',
                v => (v && v.length >= 2) || 'Please provide a valid city',
            ],
            countryRules: [
                v => !!v || 'Country is required',
                v => (v && v.length >= 2) || 'Please provide a valid country',
            ],
            dateOfBirthMenu: false,
            dateOfBirthRules: [
                v => !!v || 'Date of birth is required',
            ],
            emailRules: [
                v => !!v || 'Email is required',
                v => (v && v.length >= 2) || 'Please provide a valid email',
            ],
            personalEmailRules: [
                v => !!v || 'Email (Personal) is required',
                v => (v && v.length >= 2) || 'Please provide a valid email',
            ],
            firstNameRules: [
                v => !!v || 'First name is required',
                v => (v && v.length >= 2) || 'First name must be more than 2 characters',
            ],
            genderItems: [{
                    text: 'Female',
                    value: 'F'
                },
                {
                    text: 'Male',
                    value: 'M'
                },
                {
                    text: 'Other',
                    value: 'Other'
                },
                {
                    text: 'Non-binary',
                    value: 'NonBinary'
                }
            ],
            genderRules: [
                v => !!v || 'Gender is required'
            ],
            pronounRules: [
                v => !!v || 'Pronoun is required',
                v => /^([A-Za-z]+)\/([A-Za-z]+)$/.test(v) || 'Pronoun should include forward slash (/)',
            ],
            homePhoneRules: [
                v => !!v || 'Home phone is required',
            ],
            internationalStudentItems: [{
                    text: 'Yes',
                    value: 'Yes'
                },
                {
                    text: 'No',
                    value: 'No'
                }
            ],
            internationalStudentRules: [
                v => !!v || 'International student is required'
            ],
            lastNameRules: [
                v => !!v || 'Last name is required',
                v => (v && v.length >= 2) || 'Last name must be more than 2 characters',
            ],
            postalCodeRules: [
                v => !!v || 'Postal code is required',
                v => (v && v.length >= 2) || 'Please provide a valid postal code',
            ],
            provinceItems: [{
                    text: 'Alberta',
                    value: 'AB'
                },
                {
                    text: 'British Columbia',
                    value: 'BC'
                },
                {
                    text: 'Manitoba',
                    value: 'MB'
                },
                {
                    text: 'New Brunswick',
                    value: 'NB'
                },
                {
                    text: 'Newfoundland and Labrador',
                    value: 'NL'
                },
                {
                    text: 'Northwest Territories',
                    value: 'NT'
                },
                {
                    text: 'Nova Scotia',
                    value: 'NS'
                },
                {
                    text: 'Nunavut',
                    value: 'NU'
                },
                {
                    text: 'Ontario',
                    value: 'ON'
                },
                {
                    text: 'Prince Edward Island',
                    value: 'PE'
                },
                {
                    text: 'Quebec',
                    value: 'QC'
                },
                {
                    text: 'Saskatchewan',
                    value: 'SK'
                },
                {
                    text: 'Yukon',
                    value: 'YT'
                },
                {
                    text: 'Other',
                    value: 'Other'
                }
            ],
            provinceRules: [
                v => !!v || 'Province is required'
            ],
            provinceOtherRules: [
                v => !!v || 'Province (other) is required',
                v => (v && v.length >= 2) || 'Please provide a valid province (other)',
            ],
            registeredAtRules: [
                v => !!v || 'Registered at is required'
            ],
            residenceTermRules: [
                v => !!v || 'Residence term is required'
            ],
            studentnumberRules: [
                v => !!v || 'Student number is required',
                v => /^\d{9}$/.test(v) || 'Please provide a valid student number (9 digits)',
            ],
            valid: true,
            yearOfStudyRules: [
                v => !!v || 'Year of study is required'
            ],
            sixftoroverdetailitems: [
                {text: "Please Select...", value: ""},
                {text: "6' 0”", value: "6.0"},
                {text: "6' 1”", value: "6.1"},
                {text: "6' 2”", value: "6.2"},
                {text: "6' 3”", value: "6.3"},
                {text: "6' 4”", value: "6.4"},
                {text: "6' 5”", value: "6.5"}
            ],
            showheightoptions: false
        }
    },
    methods: {
        saveDateOfBirth() {
            this.$refs.menu.save(this.item.dateOfBirth)
        },
        showHeightOptions(){
            const that = this;
            
            if(that.item.sixftorover == 'yes'){
                that.showheightoptions = true;
            }else {
                that.item.sixftoroverdetail = '';
                
                that.showheightoptions = false;
            }
        }
    },
    computed: {
        getResidenceTerms() {
            let residenceTerms = [];

            if (this.formSettings.residenceTerms && this.residenceTermsAppliedTo) {
                for (let i = 0; i < this.formSettings.residenceTerms.length; i++) {
                    let bool = true;
                    let term = this.formSettings.residenceTerms[i].value;

                    for (let j = 0; j < this.residenceTermsAppliedTo.length; j++) {
                        if (term == this.residenceTermsAppliedTo[j].residenceTerm) {
                            bool = false;
                        }
                    }

                    if (bool) {
                        residenceTerms.push(this.formSettings.residenceTerms[i])
                    }
                }
            }

            return residenceTerms;
        },
        kingsGold() {
            return '#d7ae28';
        },
        successColor() {
            return '#1dc9b7';
        }
    },
    created(){
        this.showHeightOptions();
    }
}
</script>

<style scoped>
.col-12 {
    padding-bottom: 0;
    padding-top: 0;
}
</style>